import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Search, Filter, X, Globe, DollarSign, Eye, ChevronUp, ChevronDown } from 'lucide-react';
import { useExpiringDomains } from '@/hooks/useExpiringDomains';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import SEO from '@/components/SEO';

type SortField = 'domain_name' | 'price' | 'expiring_date' | 'created_date' | 'tld';
type SortOrder = 'asc' | 'desc';

const ExpiringDomains: React.FC = () => {
  const navigate = useNavigate();
  const { data, isLoading: loading, error } = useExpiringDomains();
  const domains = data ?? [];
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Filter states
  const [selectedTlds, setSelectedTlds] = useState<string[]>([]);
  const [priceMin, setPriceMin] = useState<number | ''>('');
  const [priceMax, setPriceMax] = useState<number | ''>('');
  const [expiringDateFrom, setExpiringDateFrom] = useState<string>('');
  const [expiringDateTo, setExpiringDateTo] = useState<string>('');

  // Pagination and sorting
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sortField, setSortField] = useState<SortField>('expiring_date');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

  // Get unique TLDs for filter
  const availableTlds = useMemo(() => {
    const tldSet = new Set<string>();
    domains.forEach(domain => {
      if (domain.tld) {
        tldSet.add(domain.tld);
      }
    });
    return Array.from(tldSet).sort();
  }, [domains]);

  // Filter and sort domains
  const filteredAndSortedDomains = useMemo(() => {
    let filtered = [...domains];

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(domain =>
        domain.domain_name.toLowerCase().includes(searchLower)
      );
    }

    // TLD filter
    if (selectedTlds.length > 0) {
      filtered = filtered.filter(domain =>
        selectedTlds.includes(domain.tld)
      );
    }

    // Price filter
    if (priceMin !== '') {
      filtered = filtered.filter(domain => domain.price >= priceMin);
    }
    if (priceMax !== '') {
      filtered = filtered.filter(domain => domain.price <= priceMax);
    }

    // Expiring date filter
    if (expiringDateFrom) {
      const fromDate = new Date(expiringDateFrom);
      filtered = filtered.filter(domain => {
        const expiringDate = new Date(domain.expiring_date);
        return expiringDate >= fromDate;
      });
    }
    if (expiringDateTo) {
      const toDate = new Date(expiringDateTo);
      toDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter(domain => {
        const expiringDate = new Date(domain.expiring_date);
        return expiringDate <= toDate;
      });
    }

    // Sorting
    filtered.sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case 'domain_name':
          aValue = a.domain_name.toLowerCase();
          bValue = b.domain_name.toLowerCase();
          break;
        case 'price':
          aValue = a.price;
          bValue = b.price;
          break;
        case 'expiring_date':
          aValue = new Date(a.expiring_date).getTime();
          bValue = new Date(b.expiring_date).getTime();
          break;
        case 'created_date':
          aValue = new Date(a.created_date).getTime();
          bValue = new Date(b.created_date).getTime();
          break;
        case 'tld':
          aValue = a.tld.toLowerCase();
          bValue = b.tld.toLowerCase();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [domains, searchTerm, selectedTlds, priceMin, priceMax, expiringDateFrom, expiringDateTo, sortField, sortOrder]);

  // Pagination
  const paginatedDomains = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredAndSortedDomains.slice(start, start + rowsPerPage);
  }, [filteredAndSortedDomains, page, rowsPerPage]);

  const totalPages = Math.ceil(filteredAndSortedDomains.length / rowsPerPage);

  // Handle TLD toggle
  const toggleTld = (tld: string) => {
    setSelectedTlds(prev =>
      prev.includes(tld) ? prev.filter(t => t !== tld) : [...prev, tld]
    );
    setPage(0);
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('');
    setSelectedTlds([]);
    setPriceMin('');
    setPriceMax('');
    setExpiringDateFrom('');
    setExpiringDateTo('');
    setPage(0);
  };

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (searchTerm) count++;
    if (selectedTlds.length > 0) count++;
    if (priceMin !== '' || priceMax !== '') count++;
    if (expiringDateFrom || expiringDateTo) count++;
    return count;
  }, [searchTerm, selectedTlds.length, priceMin, priceMax, expiringDateFrom, expiringDateTo]);

  // Format date
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  // Format price
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(price);
  };

  // Handle sorting
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
    setPage(0);
  };

  // Get sort icon
  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return null;
    return sortOrder === 'asc' ? (
      <ChevronUp className="h-4 w-4 inline ml-1" />
    ) : (
      <ChevronDown className="h-4 w-4 inline ml-1" />
    );
  };

  // Handle view details
  const handleViewDetails = (domainName: string) => {
    navigate(`/domain/${encodeURIComponent(domainName)}`);
  };

  // Generate dynamic description
  const getDescription = () => {
    const count = filteredAndSortedDomains.length;
    if (count === domains.length) {
      return `Browse ${count} expiring domains available for registration. Find domains expiring soon with pricing information, TLD filters, and detailed domain information.`;
    }
    return `Showing ${count} of ${domains.length} expiring domains. Find domains expiring soon with pricing information, TLD filters, and detailed domain information.`;
  };

  return (
    <>
      <SEO
        title="Expiring Domains - Find Domains About to Expire | DomBase"
        description={getDescription()}
        canonical="/expiring"
        ogTitle="Expiring Domains - Find Domains About to Expire | DomBase"
        ogDescription={getDescription()}
        ogUrl="/expiring"
        ogType="website"
        ogImage="/og.png"
        twitterTitle="Expiring Domains - Find Domains About to Expire | DomBase"
        twitterDescription={getDescription()}
        twitterCard="summary_large_image"
        twitterImage="/og.png"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: 'Expiring Domains - DomBase',
          description: 'Browse expiring domains available for registration',
          url: 'https://dombase.xyz/expiring'
        }}
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Expiring Domains', url: '/expiring' }
        ]}
      />
      {/* Page header */}
      <div className="border-b border-gray-100 bg-white px-4 sm:px-6 lg:px-8 py-6">
        <div className="max-w-7xl mx-auto flex items-center gap-2.5">
          <Calendar className="h-5 w-5 text-gray-400 flex-shrink-0" />
          <div>
            <h1 className="text-xl font-bold text-gray-900">Expiring Domains</h1>
            <p className="text-sm text-gray-400 mt-0.5">Browse domains expiring soon and available for registration</p>
          </div>
        </div>
      </div>

      <div className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-2">
          <p className="text-sm text-gray-500 leading-relaxed">
            Browse domain names expiring soon across all major TLDs. Expiring domains can be acquired at registration price before they enter the aftermarket — a key strategy for domain investors and founders looking for premium names.
          </p>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">

          {/* Search and Filter Bar */}
          <Card className="mb-6 bg-white border border-gray-200">
            <CardContent className="pt-6">
              <div className="flex flex-col gap-4">
                {/* Search Input */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Search domains..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setPage(0);
                    }}
                    className="pl-10"
                  />
                </div>

                {/* Filter Toggle */}
                <div className="flex items-center justify-between">
                  <Button
                    variant="outline"
                    onClick={() => setShowFilters(!showFilters)}
                    className="flex items-center gap-2"
                  >
                    <Filter className="h-4 w-4" />
                    Advanced Filters
                    {activeFilterCount > 0 && (
                      <Badge variant="secondary" className="ml-2">
                        {activeFilterCount}
                      </Badge>
                    )}
                  </Button>
                  {activeFilterCount > 0 && (
                    <Button variant="ghost" onClick={clearFilters} size="sm">
                      <X className="h-4 w-4 mr-2" />
                      Clear Filters
                    </Button>
                  )}
                </div>

                {/* Advanced Filters */}
                {showFilters && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t border-gray-200">
                    {/* TLD Filter */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Globe className="h-4 w-4 inline mr-1" />
                        TLD
                      </label>
                      <div className="max-h-32 overflow-y-auto space-y-1">
                        {availableTlds.slice(0, 10).map(tld => (
                          <label key={tld} className="flex items-center space-x-2 text-sm">
                            <input
                              type="checkbox"
                              checked={selectedTlds.includes(tld)}
                              onChange={() => toggleTld(tld)}
                              className="rounded border-gray-300"
                            />
                            <span className="text-gray-700">.{tld}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Price Range */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <DollarSign className="h-4 w-4 inline mr-1" />
                        Price Range
                      </label>
                      <div className="flex gap-2">
                        <Input
                          type="number"
                          placeholder="Min"
                          value={priceMin}
                          onChange={(e) => {
                            setPriceMin(e.target.value === '' ? '' : parseFloat(e.target.value));
                            setPage(0);
                          }}
                          className="w-full"
                        />
                        <Input
                          type="number"
                          placeholder="Max"
                          value={priceMax}
                          onChange={(e) => {
                            setPriceMax(e.target.value === '' ? '' : parseFloat(e.target.value));
                            setPage(0);
                          }}
                          className="w-full"
                        />
                      </div>
                    </div>

                    {/* Expiring Date Range */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Calendar className="h-4 w-4 inline mr-1" />
                        Expiring From
                      </label>
                      <Input
                        type="date"
                        value={expiringDateFrom}
                        onChange={(e) => {
                          setExpiringDateFrom(e.target.value);
                          setPage(0);
                        }}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Calendar className="h-4 w-4 inline mr-1" />
                        Expiring To
                      </label>
                      <Input
                        type="date"
                        value={expiringDateTo}
                        onChange={(e) => {
                          setExpiringDateTo(e.target.value);
                          setPage(0);
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Results Summary */}
          <div className="mb-4 text-sm text-gray-600">
            Showing {paginatedDomains.length} of {filteredAndSortedDomains.length} domains
            {filteredAndSortedDomains.length !== domains.length && ` (filtered from ${domains.length} total)`}
          </div>

          {/* Loading State */}
          {loading && (
            <Card className="bg-white border border-gray-200">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-blue-600" />
                  Loading...
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {[...Array(10)].map((_, i) => (
                    <div key={i} className="h-16 bg-gray-200 rounded animate-pulse" />
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className="flex-1 flex items-center justify-center py-20">
              <p className="text-sm text-red-500">Failed to load. Please try again.</p>
            </div>
          )}

          {/* Domains Table */}
          {!loading && !error && (
            <Card className="bg-white border border-gray-200">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-blue-600" />
                  Expiring Domains
                </CardTitle>
                <p className="text-sm text-gray-600 mt-1">
                  {filteredAndSortedDomains.length} domain{filteredAndSortedDomains.length !== 1 ? 's' : ''} found
                </p>
              </CardHeader>
              <CardContent className="p-0">
                {paginatedDomains.length === 0 ? (
                  <div className="p-8 text-center text-gray-600">
                    No domains found matching your filters.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                            #
                          </th>
                          <th
                            className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider min-w-[200px] cursor-pointer hover:bg-gray-100 transition-colors"
                            onClick={() => handleSort('domain_name')}
                          >
                            Domain Name{getSortIcon('domain_name')}
                          </th>
                          <th
                            className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                            onClick={() => handleSort('tld')}
                          >
                            TLD{getSortIcon('tld')}
                          </th>
                          <th
                            className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                            onClick={() => handleSort('price')}
                          >
                            Price{getSortIcon('price')}
                          </th>
                          <th
                            className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider min-w-[140px] cursor-pointer hover:bg-gray-100 transition-colors"
                            onClick={() => handleSort('created_date')}
                          >
                            Created Date{getSortIcon('created_date')}
                          </th>
                          <th
                            className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider min-w-[140px] cursor-pointer hover:bg-gray-100 transition-colors"
                            onClick={() => handleSort('expiring_date')}
                          >
                            Expiring Date{getSortIcon('expiring_date')}
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {paginatedDomains.map((domain, index) => (
                          <tr
                            key={`${domain.domain_name}-${index}`}
                            className="hover:bg-gray-50 transition-colors cursor-pointer"
                            onClick={() => handleViewDetails(domain.domain_name)}
                          >
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-500 text-white font-bold text-sm">
                                {page * rowsPerPage + index + 1}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-semibold text-gray-900">
                                {domain.domain_name}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <Badge variant="secondary" className="text-xs">
                                .{domain.tld}
                              </Badge>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">
                                {formatPrice(domain.price)}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-600">
                                {formatDate(domain.created_date)}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-600">
                                {formatDate(domain.expiring_date)}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewDetails(domain.domain_name);
                                }}
                                className="flex items-center gap-2"
                              >
                                <Eye className="h-4 w-4" />
                                View Details
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="mt-6 pt-6 border-t border-gray-200 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">Rows per page:</span>
                      <select
                        value={rowsPerPage}
                        onChange={(e) => {
                          setRowsPerPage(Number(e.target.value));
                          setPage(0);
                        }}
                        className="border border-gray-300 rounded px-2 py-1 bg-white text-gray-900 text-sm"
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(Math.max(0, page - 1))}
                        disabled={page === 0}
                      >
                        Previous
                      </Button>
                      <span className="text-sm text-gray-600">
                        Page {page + 1} of {totalPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                        disabled={page >= totalPages - 1}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </>
  );
};

export default ExpiringDomains;
